import React, {useState} from 'react';
import Toggle from "../Form/Toggle";
import {useOperator} from "../../context/OperatorContext";
import {plural} from "pluralize";
import {useMapLayers} from "./useMapLayers";
import {MapLayer} from "../../types/MapLayers";
import {toTitleCase} from "../../services/string";

// Constants ----

const layerToggleColor: Partial<Record<MapLayer, string>> = {
  [MapLayer.SITE]: 'bg-gray-400',
  [MapLayer.ZONE]: 'bg-gray-500',
  [MapLayer.INTERACTION_AREA]: 'bg-orange-500',
}

// Types ----

type LayerControlProps = {
  selected?: MapLayer[]
  onSelect?: (mapLayers: MapLayer[]) => void
}

// Component ----

function LayerControl({onSelect, selected}: LayerControlProps) {
  const {termFor} = useOperator();
  const {enabledMapLayers} = useMapLayers();

  // Keep track of the currently visible layers
  const [visibleLayers, setVisibleLayers] = useState<MapLayer[]>(selected ?? enabledMapLayers);

  // Get the display name for a layer
  function layerName(layerId: MapLayer): string {
    const name = layerId
      .split('-')
      .filter(segment => segment !== 'ngage')
      .join(" ")

    return toTitleCase(termFor(plural(name)));
  }

  // Toggle the visibility of the chosen layer
  function toggleLayer(layerId: MapLayer, enabled: boolean) {
    let newVisibleLayers = [...visibleLayers]

    if (enabled) {
      newVisibleLayers.push(layerId)
    } else {
      newVisibleLayers = newVisibleLayers.filter(visibleLayer => visibleLayer !== layerId)
    }

    setVisibleLayers(newVisibleLayers)

    if(onSelect) onSelect(newVisibleLayers)
  }

  return (
    <div className="absolute bottom-10 left-2 bg-white rounded-md px-2.5 py-1 shadow-border">

      <span className="font-semibold text-sm">Layers</span>

      {enabledMapLayers.map(layerId => {
        const color = layerToggleColor[layerId] ?? 'bg-gray-700';
        
        return (
          <div className="my-2" key={layerId}>
            <Toggle
              label={layerName(layerId)}
              checked={visibleLayers.includes(layerId)}
              onChange={checked => toggleLayer(layerId, checked)}
              short
              colorClass={color}
            />
          </div>
        )
      })}
    </div>
  );
}

export default LayerControl;